import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { emailService } from '@/services/emailService';
import type { ClientEmailDomain, EmailDomainStatus } from '@/types/email';
import type { Client } from '@/types/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';

function statusVariant(status?: EmailDomainStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'VERIFICADO') return 'default';
  if (status === 'FALHOU') return 'destructive';
  return 'outline';
}

interface Props {
  client: Client;
}

export default function ClientEmailDomainSection({ client }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const clientId = client.id;

  const [dominio, setDominio] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [replyTo, setReplyTo] = useState('');

  const queryKey = ['client-email-dominio', clientId];

  const { data: config, isLoading, isError } = useQuery<ClientEmailDomain | null>({
    queryKey,
    queryFn: () => emailService.getClientDominio(clientId),
    enabled: !!clientId,
  });

  const salvar = useMutation({
    mutationFn: () =>
      emailService.setClientDominio(clientId, {
        dominio,
        from_email: fromEmail || undefined,
        from_name: fromName || undefined,
        reply_to: replyTo || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'Domínio configurado', description: 'Adicione os registros DKIM no DNS para verificar.' });
      setDominio(''); setFromEmail(''); setFromName(''); setReplyTo('');
    },
    onError: () => toast({ title: 'Erro', description: 'Falha ao configurar domínio.', variant: 'destructive' }),
  });

  const verificar = useMutation({
    mutationFn: () => emailService.verificarClientDominio(clientId),
    onSuccess: (d) => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'Status atualizado', description: `Status atual: ${d.status}` });
    },
    onError: () => toast({ title: 'Erro', description: 'Falha ao consultar status.', variant: 'destructive' }),
  });

  const remover = useMutation({
    mutationFn: () => emailService.removerClientDominio(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'Domínio removido' });
    },
    onError: () => toast({ title: 'Erro', description: 'Falha ao remover domínio.', variant: 'destructive' }),
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Carregando...</p>;
  }
  if (isError) {
    return <p className="text-sm text-destructive">Erro ao carregar o domínio de envio do cliente.</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900">Domínio de Email do cliente</h3>
        <p className="text-sm text-gray-600 mt-1">
          Configure o domínio remetente deste cliente. Sem domínio próprio verificado, as campanhas de email saem do domínio global da Átomos.
        </p>
      </div>

      {!config && (
        <Card>
          <CardHeader><CardTitle>Configurar domínio de envio</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dominio">Domínio</Label>
                <Input id="dominio" placeholder="mail.cliente.com.br" value={dominio} onChange={(e) => setDominio(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="from_email">Email remetente padrão (From)</Label>
                <Input id="from_email" placeholder="cobranca@mail.cliente.com.br" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="from_name">Nome do remetente padrão</Label>
                <Input id="from_name" placeholder="Nome do Cliente" value={fromName} onChange={(e) => setFromName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="reply_to">Reply-To padrão</Label>
                <Input id="reply_to" placeholder="financeiro@cliente.com.br" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} />
              </div>
            </div>
            <Button onClick={() => salvar.mutate()} disabled={!dominio || salvar.isPending}>
              {salvar.isPending ? 'Configurando...' : 'Configurar e gerar DKIM'}
            </Button>
          </CardContent>
        </Card>
      )}

      {config && (
        <Card>
          <CardHeader><CardTitle>Domínio de envio</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-medium">{config.dominio}</p>
                {(config.from_name || config.from_email) && (
                  <p className="text-sm text-muted-foreground">
                    {config.from_name} {config.from_email ? `<${config.from_email}>` : ''}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={statusVariant(config.status)}>{config.status}</Badge>
                <Button size="sm" variant="outline" onClick={() => verificar.mutate()} disabled={verificar.isPending || remover.isPending}>
                  Verificar agora
                </Button>
                <Button size="sm" variant="destructive" onClick={() => remover.mutate()} disabled={verificar.isPending || remover.isPending}>
                  Remover
                </Button>
              </div>
            </div>

            {config.dkim_tokens?.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Registros DKIM (adicione no DNS do domínio):</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {config.dkim_tokens.map((rec) => (
                      <TableRow key={rec.name}>
                        <TableCell>{rec.type}</TableCell>
                        <TableCell className="font-mono text-xs break-all">{rec.name}</TableCell>
                        <TableCell className="font-mono text-xs break-all">{rec.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
